import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, BarChart3, Mic, Volume2, Turtle, Square, RotateCcw, Target, Sparkles } from 'lucide-react';
import ScoreRing from '../components/brand/ScoreRing';
import { tone, level } from '../lib/score';
import { ACCENT_FLAGS } from '../lib/audio';
import { useSpeaker } from '../lib/speak';
import { soundInfo } from '../lib/phonetics';
import { sessionCoach, wordGuide, recommend } from '../lib/coach';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Backend (Azure) payload → the shape this page renders.
const normalize = (parsed) => {
  const scores = parsed.scores || {};
  const prosody = parsed.prosody_analysis || {};
  return {
    transcript: parsed.transcript || '',
    referenceText: parsed.reference_text || '',
    overallScore: parsed.overall_score || scores.overall || 0,
    scores: {
      pronunciation: scores.pronunciation || 0,
      accuracy: scores.accuracy || 0,
      fluency: scores.fluency || 0,
      completeness: scores.completeness || 0,
      prosody: scores.prosody || 0,
    },
    accentDetected: parsed.accent_detected || { accent: 'Unknown', confidence: 0 },
    prosody: {
      intonation: prosody.intonation_score || 0,
      stress: prosody.stress_score || 0,
      rhythm: prosody.rhythm_score || 0,
      speakingRate: prosody.speaking_rate_wpm || 0,
    },
    wordAnalysis: parsed.pronunciation_analysis || [],
    phonemeBreakdown: parsed.phoneme_breakdown || [],
  };
};

const readJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const METRICS = [
  ['pronunciation', 'Pronunciation'],
  ['accuracy', 'Accuracy'],
  ['fluency', 'Fluency'],
  ['completeness', 'Completeness'],
  ['prosody', 'Prosody'],
];

const MetricTile = ({ label, score }) => {
  const t = tone(score);
  return (
    <div className="cz-card p-4 last:col-span-2 sm:last:col-span-1" data-testid={`score-card-${label.toLowerCase()}`}>
      <p className="text-xs text-white/45">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${t.text}`}>{Math.round(score)}</p>
      <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/[0.06]">
        <div className={`h-full rounded-full bg-gradient-to-r ${t.bar}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
};

const PlayButtons = ({ speaker, text, size = 'h-8 w-8' }) =>
  speaker.canSpeak ? (
    <span className="inline-flex gap-1.5">
      <button type="button" onClick={() => speaker.speak(text)} className={`grid ${size} place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20`} aria-label={`Hear ${text}`}>
        <Volume2 className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => speaker.speak(text, { slow: true })} className={`grid ${size} place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20`} aria-label={`Hear ${text} slowly`} title="Slowly">
        <Turtle className="h-4 w-4" />
      </button>
    </span>
  ) : null;

const wordClass = (w) => {
  if (w.error_type === 'Omission') return 'text-rose-300 decoration-rose-400/70 line-through';
  if (w.score >= 80) return 'text-emerald-200 decoration-emerald-400/50';
  if (w.score >= 60) return 'text-amber-200 decoration-amber-400/60';
  return 'text-rose-200 decoration-rose-400/70';
};

// "Fix these first": the weakest words, each with how to say it.
const FixCard = ({ item, speaker }) => {
  const t = tone(item.score);
  const w = item.weakest;
  return (
    <div className={`rounded-2xl border p-5 ${t.soft}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-2xl font-semibold text-white">{item.word}</p>
          {item.ipa && <p className="mt-0.5 font-mono text-sm text-white/55">{item.ipa}</p>}
        </div>
        <PlayButtons speaker={speaker} text={item.word} />
      </div>
      {item.say && (
        <p className="mt-3 text-sm text-white/70">Sounds like <span className="font-semibold tracking-wide text-white">{item.say}</span></p>
      )}
      <p className="mt-3 text-sm leading-relaxed text-white/75">
        {item.omitted
          ? 'We didn’t hear this one. Say it out loud on its own a few times, then put it back in the sentence.'
          : w
            ? <><span className="font-semibold text-white">“{w.info.say}” /{w.info.ipa}/ scored {Math.round(w.acc)}.</span> {w.info.tip}</>
            : 'Close. Listen to it slowly and copy the vowel length.'}
      </p>
      <p className="mt-3 text-xs text-white/40">Score {Math.round(item.score)}/100</p>
    </div>
  );
};

const WordInspector = ({ words, phonemes, passage, speaker }) => {
  const guides = useMemo(() => words.map((w, i) => wordGuide(w, i, phonemes)), [words, phonemes]);
  // start on the weakest word — that's what the user wants to see first
  const weakest = useMemo(
    () => words.reduce((best, w, i) => (w.score < words[best].score ? i : best), 0),
    [words]
  );
  const [sel, setSel] = useState(weakest);
  const pick = (i) => {
    setSel(i);
    if (speaker.canSpeak) speaker.speak(words[i].word);
  };
  const long = words.length > 20;
  const hearingAll = passage && speaker.speaking === passage;
  const w = words[sel];
  const g = guides[sel];
  const ph = phonemes[sel]?.word?.toLowerCase() === w.word?.toLowerCase()
    ? phonemes[sel]
    : phonemes.find((p) => p.word?.toLowerCase() === w.word?.toLowerCase());
  const t = tone(w.score);

  return (
    <div className="cz-card p-6 sm:p-8" data-testid="word-analysis-card">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="font-semibold text-white">{long ? 'Your reading, word by word' : 'Your sentence, word by word'}</h2>
        {speaker.canSpeak && passage && (
          <button
            type="button"
            onClick={() => (hearingAll ? speaker.stop() : speaker.speak(passage))}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs font-medium text-white/75 transition-colors hover:bg-white/5"
          >
            {hearingAll ? <><Square className="h-3 w-3 fill-current" /> Stop</> : <><Volume2 className="h-3.5 w-3.5" /> Hear it all</>}
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-white/40">
        <span className="hidden md:inline">Hover a coloured word for how to say it. </span>
        {speaker.canSpeak ? 'Tap any word to hear it.' : 'Tap any word to see its sounds.'}
      </p>
      <p className={`mt-4 tracking-[-0.01em] ${long ? 'text-lg leading-[2.1] sm:text-xl' : 'text-2xl leading-[1.7] sm:text-3xl'}`} data-testid="word-analysis-list">
        {words.map((word, i) => {
          const gi = guides[i];
          const tip = word.score < 80 && (gi.ipa || word.error_type === 'Omission');
          return (
            <React.Fragment key={i}>
              <span className="group relative inline-block">
                <button
                  type="button"
                  onClick={() => pick(i)}
                  className={`rounded-md px-1 font-medium underline decoration-2 underline-offset-[6px] transition-colors ${wordClass(word)} ${
                    sel === i ? 'bg-white/10' : 'hover:bg-white/5'
                  }`}
                  data-testid={`word-${i}`}
                >
                  {word.word}
                </button>
                {tip && (
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-60 -translate-x-1/2 rounded-xl border border-white/10 bg-ink-800 p-3 text-left text-xs font-normal leading-relaxed tracking-normal text-white/75 shadow-2xl md:group-hover:block"
                  >
                    <span className="flex items-baseline justify-between">
                      <span className="text-sm font-semibold text-white">{word.word}</span>
                      <span className={tone(word.score).text}>{Math.round(word.score)}</span>
                    </span>
                    {gi.ipa && <span className="mt-0.5 block font-mono text-white/50">{gi.ipa}</span>}
                    {gi.say && <span className="mt-1 block">Sounds like <b className="text-white">{gi.say}</b></span>}
                    {word.error_type === 'Omission'
                      ? <span className="mt-1 block">Skipped — say it on its own, then in the sentence.</span>
                      : gi.weakest && <span className="mt-1 block"><b className="text-white">/{gi.weakest.info.ipa}/:</b> {gi.weakest.info.tip}</span>}
                  </span>
                )}
              </span>{' '}
            </React.Fragment>
          );
        })}
      </p>

      <div className={`mt-6 rounded-2xl border p-5 ${t.soft}`} aria-live="polite">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <p className="text-xl font-semibold text-white">{w.word}</p>
            <PlayButtons speaker={speaker} text={w.word} />
          </div>
          <p className="text-sm">
            <span className={`text-2xl font-semibold tabular-nums ${t.text}`}>{Math.round(w.score)}</span>
            <span className="text-white/40"> / 100</span>
            {w.error_type && w.error_type !== 'None' && <span className="ml-3 text-white/60">{w.error_type}</span>}
          </p>
        </div>
        {(g.ipa || g.say) && (
          <p className="mt-2 text-sm text-white/65">
            {g.ipa && <span className="font-mono text-white/55">{g.ipa}</span>}
            {g.say && <> · sounds like <b className="text-white">{g.say}</b></>}
          </p>
        )}
        {ph?.phonemes?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {ph.phonemes.map((p, i) => {
              const acc = ph.phoneme_scores?.[i]?.accuracy || 0;
              const inf = soundInfo(p);
              return (
                <span key={i} className={`rounded-lg border px-2.5 py-1 font-mono text-sm ${tone(acc).soft}`} title={inf ? `${inf.say} as in “${inf.ex}” — ${inf.tip}` : `Accuracy ${Math.round(acc)}%`}>
                  {inf?.ipa || p}<span className="ml-1.5 text-[10px] opacity-60">{Math.round(acc)}</span>
                </span>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-sm leading-relaxed text-white/70">
          {w.error_type === 'Omission'
            ? 'We didn’t hear this word. Say it on its own first, then in the sentence.'
            : g.weakest
              ? <><b className="text-white">“{g.weakest.info.say}” /{g.weakest.info.ipa}/ was probably {g.weakest.info.slip}.</b> {g.weakest.info.tip}</>
              : w.score >= 80 ? 'Clean — nothing to fix here.' : 'Close. Listen to it slowly and copy the vowel length.'}
        </p>
      </div>

      <div className="mt-4 flex flex-wrap gap-4 text-xs text-white/40">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-400" />80+</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-400" />60–79</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-rose-400" />under 60</span>
      </div>
    </div>
  );
};

const paceNote = (wpm) => {
  if (!wpm) return '';
  if (wpm > 170) return 'A little fast — aim for about 140.';
  if (wpm < 105) return 'On the slow side — read in phrases.';
  return 'A comfortable pace to follow.';
};

const Results = () => {
  const navigate = useNavigate();
  const [result] = useState(() => {
    const raw = readJSON('analysisResult');
    return raw ? normalize(raw) : null;
  });
  const [exercise] = useState(() => readJSON('analysisExercise'));
  const [catalog, setCatalog] = useState([]);
  const speaker = useSpeaker();
  const coach = useMemo(() => (result ? sessionCoach(result) : null), [result]);

  useEffect(() => {
    axios.get(`${API}/exercises`).then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  if (!result) {
    return (
      <div className="mx-auto grid min-h-[60vh] max-w-md place-items-center px-5 text-center">
        <div>
          <Mic className="mx-auto h-10 w-10 text-brand-400" />
          <h1 className="mt-4 text-xl font-semibold text-white">No results yet</h1>
          <p className="mt-2 text-sm text-white/55">Record a practice sentence and your scores will show up here.</p>
          <Link to="/practice" className="cz-btn-primary mt-6">Start practicing</Link>
        </div>
      </div>
    );
  }

  const { accentDetected: accent, prosody } = result;
  const prosodyTiles = [
    ['Intonation', prosody.intonation, 'Pitch movement'],
    ['Stress', prosody.stress, 'Emphasis on the right syllables'],
    ['Rhythm', prosody.rhythm, 'Timing between words'],
  ];
  const retryTo = exercise?.id ? `/practice?ex=${exercise.id}` : '/practice';
  const upNext = catalog.length ? recommend(catalog, coach.focus, { exclude: exercise?.id, n: 2 }) : [];

  return (
    <div className="mx-auto max-w-5xl px-5 pt-6 sm:pt-10" data-testid="results-page">
      <button type="button" onClick={() => navigate('/practice')} className="inline-flex items-center gap-2 text-sm text-white/50 transition-colors hover:text-white" data-testid="back-to-practice-btn">
        <ArrowLeft className="h-4 w-4" /> Practice
      </button>

      <section className="mt-6 flex flex-col items-center gap-8 md:flex-row md:items-center md:gap-12" data-testid="overall-score-section">
        <ScoreRing score={result.overallScore} />
        <div className="text-center md:text-left animate-fade-up">
          <p className="cz-eyebrow">{exercise?.title || 'Your result'}</p>
          <h1 className="mt-2 text-4xl font-semibold tracking-[-0.03em] text-white sm:text-5xl" data-testid="results-heading">
            <span className="cz-display" data-testid="performance-level">{level(result.overallScore)}</span>
          </h1>
          <p className="mt-3 max-w-md text-lg leading-relaxed text-white/70">{coach.verdict}</p>
        </div>
      </section>

      <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="score-breakdown-grid">
        {METRICS.map(([k, label]) => <MetricTile key={k} label={label} score={result.scores[k]} />)}
      </div>

      {coach.fixFirst.length > 0 && (
        <section className="mt-10">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-coral-400" />
            <h2 className="text-lg font-semibold text-white">Fix these first</h2>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            {coach.fixFirst.map((item) => <FixCard key={item.word} item={item} speaker={speaker} />)}
          </div>
        </section>
      )}

      {result.wordAnalysis.length > 0 && (
        <div className="mt-4">
          <WordInspector words={result.wordAnalysis} phonemes={result.phonemeBreakdown} passage={result.referenceText} speaker={speaker} />
        </div>
      )}

      <section className="cz-card mt-4 border-brand-400/20 bg-brand-500/[0.06] p-6 sm:p-8" data-testid="suggestions-card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-white"><Sparkles className="h-4 w-4 text-brand-300" /> Your next take</h2>
            <p className="mt-1 text-sm text-white/50">Do these on your next try and watch the score move.</p>
          </div>
          <Link to={retryTo} className="cz-btn-primary shrink-0 !px-5" data-testid="try-again-btn">
            <RotateCcw className="h-4 w-4" /> Try it again
          </Link>
        </div>
        <ol className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {coach.nextTake.map((tip, i) => (
            <li key={tip.title} className="rounded-2xl bg-white/[0.04] p-5" data-testid={`suggestion-${i}`}>
              <span className="font-mono text-xs text-brand-300">0{i + 1}</span>
              <p className="mt-2 font-semibold text-white">{tip.title}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-white/65">{tip.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[1fr_280px]">
        <div className="cz-card p-6" data-testid="prosody-card">
          <h2 className="font-semibold text-white">Melody & rhythm</h2>
          <div className="mt-5 space-y-4">
            {prosodyTiles.map(([label, score, sub]) => {
              const t = tone(score);
              return (
                <div key={label}>
                  <div className="mb-1.5 flex items-baseline justify-between">
                    <span className="text-sm text-white/80">{label} <span className="ml-1 text-xs text-white/35">{sub}</span></span>
                    <span className={`font-semibold tabular-nums ${t.text}`} data-testid={`${label.toLowerCase()}-score`}>{Math.round(score)}</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
                    <div className={`h-full rounded-full bg-gradient-to-r ${t.bar}`} style={{ width: `${score}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="cz-card flex flex-col justify-center p-6 text-center">
          <p className="text-xs text-white/45">Speaking rate</p>
          <p className="mt-1 text-5xl font-semibold tabular-nums text-brand-300" data-testid="speaking-rate">{Math.round(prosody.speakingRate)}</p>
          <p className="mt-1 text-xs text-white/40">words per minute</p>
          <p className="mt-2 text-sm text-white/65">{paceNote(prosody.speakingRate)}</p>
        </div>
      </div>

      {accent.accent && accent.accent !== 'Unknown' && (
        <div className="cz-card mt-4 flex items-center gap-5 p-6" data-testid="accent-card">
          <span className="text-5xl" data-testid="accent-flag">{ACCENT_FLAGS[accent.accent] || '🌍'}</span>
          <div className="flex-1">
            <p className="text-xs text-white/45">Accent heard</p>
            <p className="text-xl font-semibold text-white" data-testid="accent-name">{accent.accent}</p>
            {accent.confidence > 0 && <p className="text-sm text-white/50">{Math.round(accent.confidence * 100)}% confidence</p>}
          </div>
          <Link to="/accent-test" className="hidden text-sm font-semibold text-brand-300 hover:text-brand-200 sm:inline">Full accent test →</Link>
        </div>
      )}

      {upNext.length > 0 && (
        <section className="mt-10">
          <p className="cz-eyebrow">Up next · {coach.focus}</p>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            {upNext.map((ex) => (
              <Link key={ex.id} to={`/practice?ex=${ex.id}`} className="cz-card group p-5 transition-colors hover:border-white/15">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-white">{ex.title}</p>
                  <ArrowRight className="h-4 w-4 text-white/40 transition-transform group-hover:translate-x-1 group-hover:text-white" />
                </div>
                <p className="mt-2 line-clamp-2 text-sm text-white/50">{ex.target_phrase}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-testid="action-buttons">
        <Link to="/dashboard" className="cz-btn-ghost sm:flex-1" data-testid="view-dashboard-btn">
          <BarChart3 className="h-4 w-4" /> See my progress
        </Link>
        <Link to={retryTo} className="cz-btn-primary sm:flex-1" data-testid="practice-again-btn">
          <RotateCcw className="h-4 w-4" /> Try it again
        </Link>
      </div>
    </div>
  );
};

export default Results;
